import { useState, useEffect, useCallback } from 'react';
import api from '../../api/client';
import { formatPrice } from '../../utils/formatPrice';

export default function SalesReport({ onClose }) {
  const [dateFrom, setDateFrom] = useState(todayStr());
  const [dateTo, setDateTo] = useState(todayStr());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getSalesReport({ date_from: dateFrom, date_to: dateTo });
      setReport(data);
    } catch (err) {
      setError(err.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">Sales Report</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Date filters */}
        <div className="flex items-center gap-4 p-4 border-b bg-gray-50">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">From</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-pos-primary focus:border-transparent"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">To</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-pos-primary focus:border-transparent"
            />
          </div>
          <div className="flex gap-2 ml-auto">
            <button onClick={() => { setDateFrom(todayStr()); setDateTo(todayStr()); }} className="px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50">Today</button>
            <button onClick={() => { setDateFrom(daysAgo(7)); setDateTo(todayStr()); }} className="px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50">7 Days</button>
            <button onClick={() => { setDateFrom(daysAgo(30)); setDateTo(todayStr()); }} className="px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50">30 Days</button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pos-primary"></div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {report && !loading && (
            <>
              {/* Summary cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <SummaryCard label="Total Sales" value={formatPrice(report.total_sales)} />
                <SummaryCard label="Orders" value={report.order_count} />
                <SummaryCard label="Tax Collected" value={formatPrice(report.total_tax)} />
                <SummaryCard label="Discounts" value={formatPrice(report.total_discount)} />
              </div>

              {/* By payment method */}
              {report.by_payment.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">By Payment Method</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {report.by_payment.map((pm) => (
                      <div key={pm.method} className="bg-gray-50 rounded-lg p-4">
                        <div className="text-sm text-gray-500">{pm.label}</div>
                        <div className="text-xl font-bold mt-1">{formatPrice(pm.total)}</div>
                        <div className="text-xs text-gray-400 mt-1">{pm.count} order{pm.count !== 1 ? 's' : ''}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Orders list */}
              {report.orders.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Orders</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-2 px-3 font-medium text-gray-500">Order</th>
                          <th className="text-left py-2 px-3 font-medium text-gray-500">Date</th>
                          <th className="text-left py-2 px-3 font-medium text-gray-500">Customer</th>
                          <th className="text-left py-2 px-3 font-medium text-gray-500">Payment</th>
                          <th className="text-right py-2 px-3 font-medium text-gray-500">Items</th>
                          <th className="text-right py-2 px-3 font-medium text-gray-500">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {report.orders.map((order) => (
                          <tr key={order.id} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="py-2 px-3 font-medium">#{order.number}</td>
                            <td className="py-2 px-3 text-gray-500">{formatDateTime(order.date)}</td>
                            <td className="py-2 px-3">{order.customer_name || 'Guest'}</td>
                            <td className="py-2 px-3">
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                                {order.payment_title}
                              </span>
                            </td>
                            <td className="py-2 px-3 text-right">{order.items_count}</td>
                            <td className="py-2 px-3 text-right font-medium">{formatPrice(order.total)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {report.orders.length === 0 && (
                <div className="text-center py-12 text-gray-400">
                  <svg className="w-12 h-12 mx-auto mb-3 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p>No POS orders found for this period.</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ label, value }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <div className="text-sm text-gray-500">{label}</div>
      <div className="text-2xl font-bold mt-1">{value}</div>
    </div>
  );
}

function todayStr() {
  return new Date().toISOString().split('T')[0];
}

function daysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().split('T')[0];
}

function formatDateTime(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
